
import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { UploadCloud, FileSpreadsheet, Mail, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { ImportFormat, EmailImportResult } from '@/types/collective-feedback';
import { read, utils } from 'xlsx';

interface EmailImporterProps {
  onImportComplete: (result: EmailImportResult) => void;
  isLoading?: boolean;
}

const EmailImporter: React.FC<EmailImporterProps> = ({ onImportComplete, isLoading = false }) => {
  const [importTab, setImportTab] = useState<string>('paste');
  const [pasteContent, setPasteContent] = useState<string>('');
  const [importResult, setImportResult] = useState<EmailImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const parseEmails = (content: string): EmailImportResult => {
    const lines = content.split(/[\n,;]+/).map(l => l.trim()).filter(Boolean);
    const result: EmailImportResult = {
      validEmails: [],
      invalidEmails: [],
      duplicates: [],
      total: lines.length
    };

    const emailSet = new Set<string>();

    lines.forEach(line => {
      // Check if line contains name and email
      const matches = line.match(/^([^<]+)<([^>]+)>$/) || line.match(/^([^,]+),\s*(.+@.+\..+)$/);
      
      if (matches) {
        const name = matches[1].trim();
        const email = matches[2].trim().toLowerCase();
        
        if (validateEmail(email)) {
          if (emailSet.has(email)) {
            result.duplicates.push(email);
          } else {
            emailSet.add(email);
            result.validEmails.push({ email, name });
          }
        } else {
          result.invalidEmails.push(line);
        }
      } else {
        // Just an email without name
        const email = line.trim().toLowerCase();
        if (validateEmail(email)) {
          if (emailSet.has(email)) {
            result.duplicates.push(email);
          } else {
            emailSet.add(email);
            result.validEmails.push({ email });
          }
        } else {
          result.invalidEmails.push(line);
        }
      }
    });

    return result;
  };

  const parseCSVorExcel = async (file: File): Promise<EmailImportResult> => {
    try {
      const data = await file.arrayBuffer();
      const workbook = read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = utils.sheet_to_json<any>(worksheet);
      
      const result: EmailImportResult = {
        validEmails: [],
        invalidEmails: [],
        duplicates: [],
        total: jsonData.length
      };
      
      const emailSet = new Set<string>();
      
      jsonData.forEach((row: any) => {
        // Check common column names for email
        const email = (row.email || row.Email || row['E-mail'] || row.mail || row.Mail || row.courriel || 
                      row.Courriel || row['Adresse email'] || row['Adresse e-mail'] || row['E-Mail'] || 
                      row.EmailAddress || '')
                      .toString().trim().toLowerCase();
                      
        // Check common column names for name
        const firstName = (row.firstname || row.FirstName || row['Prénom'] || row.prenom || row.Prenom || row.first_name || row.First_Name || '').toString().trim();
        const lastName = (row.lastname || row.LastName || row['Nom'] || row.nom || row.last_name || row.Last_Name || '').toString().trim();
        const fullName = (row.name || row.Name || row['Nom Complet'] || row['Full Name'] || row.fullname || row.FullName || '').toString().trim();
        
        const name = fullName || [firstName, lastName].filter(Boolean).join(' ');
        
        if (validateEmail(email)) {
          if (emailSet.has(email)) {
            result.duplicates.push(email);
          } else {
            emailSet.add(email);
            result.validEmails.push({ email, ...(name ? { name } : {}) });
          }
        } else if (email) {
          result.invalidEmails.push(email);
        }
      });
      
      return result;
    } catch (err) {
      console.error('Error parsing file:', err);
      throw new Error('Impossible de lire le fichier. Vérifiez le format et réessayez.');
    }
  };

  const handlePasteImport = () => {
    setError(null);
    try {
      if (!pasteContent.trim()) {
        setError('Veuillez entrer des adresses email');
        return;
      }
      
      const result = parseEmails(pasteContent);
      setImportResult(result);
      onImportComplete(result);
    } catch (err: any) {
      setError(err.message || 'Une erreur est survenue lors de l\'importation des emails');
    }
  };

  const handleFileImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    try {
      const file = e.target.files?.[0];
      if (!file) return;
      
      const result = await parseCSVorExcel(file);
      setImportResult(result);
      onImportComplete(result);
    } catch (err: any) {
      setError(err.message || 'Une erreur est survenue lors de l\'importation du fichier');
    } finally {
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="space-y-4">
      <Tabs value={importTab} onValueChange={setImportTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="paste" className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            <span>Coller des emails</span>
          </TabsTrigger>
          <TabsTrigger value="file" className="flex items-center gap-2">
            <FileSpreadsheet className="h-4 w-4" />
            <span>Importer un fichier</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="paste" className="space-y-4">
          <Textarea
            placeholder="Collez vos adresses email ici (une par ligne, ou séparées par des virgules)"
            value={pasteContent}
            onChange={(e) => setPasteContent(e.target.value)}
            className="min-h-[200px]"
          />
          <Button 
            onClick={handlePasteImport} 
            disabled={isLoading || !pasteContent.trim()}
            className="w-full"
          >
            Importer les emails
          </Button>
        </TabsContent>

        <TabsContent value="file" className="space-y-4">
          <Card className="flex flex-col items-center justify-center p-6 border-2 border-dashed">
            <UploadCloud className="h-12 w-12 text-gray-400 mb-4" />
            <p className="text-sm text-muted-foreground mb-4">
              Cliquez ici pour sélectionner un fichier ou glissez-déposez
            </p>
            <p className="text-xs text-muted-foreground mb-6">
              Formats acceptés: .xlsx, .xls, .csv
            </p>
            <Input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileImport}
              className="max-w-xs"
            />
          </Card>
        </TabsContent>
      </Tabs>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Erreur</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {importResult && (
        <div className="space-y-3 mt-4">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-500" />
            <h3 className="font-medium">Résultat de l'importation</h3>
          </div>
          
          <div className="grid grid-cols-4 gap-2 text-sm">
            <div className="bg-gray-50 p-3 rounded-md">
              <p className="text-xs text-gray-500">Total</p>
              <p className="font-semibold">{importResult.total}</p>
            </div>
            <div className="bg-green-50 p-3 rounded-md">
              <p className="text-xs text-green-700">Valides</p>
              <p className="font-semibold text-green-700">{importResult.validEmails.length}</p>
            </div>
            <div className="bg-orange-50 p-3 rounded-md">
              <p className="text-xs text-orange-700">Doublons</p>
              <p className="font-semibold text-orange-700">{importResult.duplicates.length}</p>
            </div>
            <div className="bg-red-50 p-3 rounded-md">
              <p className="text-xs text-red-700">Invalides</p>
              <p className="font-semibold text-red-700">{importResult.invalidEmails.length}</p>
            </div>
          </div>

          <div className="border rounded-md p-3 max-h-[200px] overflow-y-auto">
            <h4 className="text-sm font-medium mb-2">Adresses valides</h4>
            <div className="flex flex-wrap gap-2">
              {importResult.validEmails.map((item, index) => (
                <Badge key={index} variant="secondary" className="text-xs">
                  {item.name ? `${item.name} <${item.email}>` : item.email}
                </Badge>
              ))}
            </div>
          </div>

          {importResult.invalidEmails.length > 0 && (
            <div className="border border-red-200 rounded-md p-3 max-h-[150px] overflow-y-auto bg-red-50">
              <h4 className="text-sm font-medium mb-2 text-red-700">Adresses invalides</h4>
              <div className="flex flex-wrap gap-2">
                {importResult.invalidEmails.map((email, index) => (
                  <Badge key={index} variant="outline" className="text-xs text-red-700 bg-red-50 border-red-200">
                    {email}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EmailImporter;
